import { useEffect, useState } from "react";
import { MsalProvider } from "@azure/msal-react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { AUTH_CALLBACK_PATH, getMsalInstance } from "./auth/msalConfig.js";
import { AuthCallback } from "./auth/AuthCallback.js";
import { AuthGate } from "./auth/AuthGate.js";
import { NoTenantAssignedError, usePortalNavEntries } from "./api/portalQueries.js";
import { PortalNav } from "./nav/PortalNav.js";
import { TenantSelectionProvider } from "./nav/TenantSelection.js";
import { useTenantBrand } from "./theme/tenantBrand.js";
import { FileDetailPage } from "./screens/FileDetailPage.js";
import { BoardSwitch, WorkflowBoardRoute } from "./screens/BoardRoute.js";
import { ImportPage } from "./screens/ImportPage.js";
import { OutstandingItemsPage } from "./screens/OutstandingItemsPage.js";
import { SalesActivityPage } from "./screens/SalesActivityPage.js";
import { VacanciesPage } from "./screens/VacanciesPage.js";
import { EmailEditorPage, EmailsPage } from "./screens/EmailsPage.js";

/**
 * Reporting cadence, not ops cadence.
 *
 * <p>The operator console polls every 4s because an operator is watching work move.
 * A client is reading a weekly report: the underlying facts change when a scheduled
 * job writes them, which is orders of magnitude slower than a person's attention
 * span. So: a 60s stale window, a refetch when the tab is focused again, and NO
 * polling interval anywhere. Every poll the portal does not make is Loxo/engine
 * load and client bandwidth it does not spend.</p>
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: true,
      retry: (failureCount) => failureCount < 1,
    },
  },
});

/**
 * MSAL Browser v3 boot, in this exact order, before any route renders — otherwise
 * AuthGate races MsalProvider for the redirect response and the client sees a
 * double prompt or a stuck "Checking your session…":
 *   1. initialize()            — required before any other MSAL call
 *   2. handleRedirectPromise() — consume an /auth/callback code exactly once
 *   3. setActiveAccount()      — pin the account so AuthGate's first effect sees it
 *
 * <p>Step 3 on a load with no redirect response (a reload, a new tab, a bookmark) KEEPS the
 * active account MSAL already persisted, as long as it is still cached. #2513: after "Switch
 * account" from A to B, MSAL caches both and lists them in insertion order, so blindly adopting
 * `getAllAccounts()[0]` would re-pin A on the next load and silently undo the switch. Only when
 * there is no (still-cached) active account do we adopt the first cached one, so a refresh after
 * signing in lands ready rather than re-prompting.</p>
 *
 * <p>Exported for the boot test, which drives it with a stand-in instance.</p>
 */
export async function bootMsal(
  msal: Pick<
    ReturnType<typeof getMsalInstance>,
    "initialize" | "handleRedirectPromise" | "getActiveAccount" | "getAllAccounts" | "setActiveAccount"
  >,
): Promise<void> {
  await msal.initialize();
  const result = await msal.handleRedirectPromise();
  if (result?.account) {
    msal.setActiveAccount(result.account);
    return;
  }
  const accounts = msal.getAllAccounts();
  const active = msal.getActiveAccount();
  if (active && accounts.some((a) => a.homeAccountId === active.homeAccountId)) return;
  const first = accounts[0];
  if (first) msal.setActiveAccount(first);
}

/**
 * Phase 342 (ADR-081 D3) — the client reporting portal shell.
 *
 * <p>Tree shape: MsalProvider → QueryClientProvider → BrowserRouter, with
 * `/auth/callback` mounted OUTSIDE {@link AuthGate} and everything else inside it.</p>
 *
 * <p>No operator screen is imported anywhere in this tree — the admin surface is
 * structurally absent from this build, not merely unrouted (ADR-081 D3).</p>
 */
export function App() {
  const [msalReady, setMsalReady] = useState(false);
  useEffect(() => {
    void bootMsal(getMsalInstance()).then(() => setMsalReady(true));
  }, []);

  if (!msalReady) return <div className="auth-loading">Loading…</div>;

  return (
    <MsalProvider instance={getMsalInstance()}>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <Routes>
            <Route path={AUTH_CALLBACK_PATH} element={<AuthCallback />} />
            <Route
              path="/*"
              element={
                <AuthGate>
                  <PortalShell />
                </AuthGate>
              }
            />
          </Routes>
        </BrowserRouter>
      </QueryClientProvider>
    </MsalProvider>
  );
}

/**
 * The signed-in portal: the nav rail plus one report page.
 *
 * <p>Exported for tests, which mount it under their own router and query client. That is
 * deliberate — it is the real composition (nav, routes and landing redirect all reading the
 * same `usePortalNavEntries` derivation), so a test of "what does a client with one
 * configured report see" exercises the production wiring rather than a stand-in for it.</p>
 *
 * <p>Both report routes exist here from task 4.1, because the nav's whole contract is that a
 * configured family resolves to a route that renders. Both now render their real page
 * (tasks 4.2 and 4.3). The routes themselves do not move: phase 336 (#1163) rewrites the two
 * pages IN PLACE, and `/sales-activity` and `/vacancies` are the client vocabulary the nav
 * and the forecast card's deep link both point at.</p>
 *
 * <p>Phase 530 wave 4 task 4.1 (#1939) adds the onboarding board's three routes on the same
 * terms, and for the same reason the report routes landed before their pages did: the registry
 * entry that makes "Onboarding" appear in the nav is only honest if the route it names renders
 * something. All three now render their real page — {@link FilesBoardPage} (task 4.2), and
 * {@link OutstandingItemsPage} + {@link FileDetailPage} (task 4.3). No placeholder is left here.</p>
 */
export function PortalShell() {
  // Walk 3: the tenant choice (operators / platform admins only — a client has one tenant and
  // resolves exactly as before) wraps the whole shell, so the rail and every page read ONE answer.
  return (
    <TenantSelectionProvider>
      <PortalShellBody />
    </TenantSelectionProvider>
  );
}

function PortalShellBody() {
  // #2663: a single-tenant client's portal wears their firm's brand; worn here, once, for the
  // whole shell (every page reads the same tokens), and the nav shows the firm's logo or name.
  // Until `/me` answers, the nav's brand block is empty: no AR Dept. mark to swap out a moment later.
  const { brand, pending } = useTenantBrand();
  return (
    <div className="portal">
      <PortalNav brand={brand} brandPending={pending} />
      <main className="portal__main">
        <div className="portal__measure">
          <Routes>
            <Route index element={<PortalLanding />} />
            <Route path="sales-activity" element={<SalesActivityPage />} />
            <Route path="vacancies" element={<VacanciesPage />} />
            {/* The onboarding status board (#1939). `outstanding` is declared BEFORE the
                param route on purpose — belt to React Router's own braces, which already rank
                a static segment above a dynamic one whatever order they are written in. Both
                mechanisms are real and neither is relied on alone: if this ever regressed,
                `/onboarding/outstanding` would silently render a FILE DETAIL asking the engine
                for a file called "outstanding", which is a 404 dressed as a page rather than a
                crash. The routing test in nav/PortalNav.test.tsx drives the literal path and
                asserts which of the two rendered. */}
            <Route path="onboarding" element={<BoardSwitch />} />
            <Route path="onboarding/outstanding" element={<OutstandingItemsPage />} />
            <Route path="onboarding/:jobId" element={<FileDetailPage />} />
            {/* #2508 (A6): one board per status_view declaration, each at its workflow's address.
                `/onboarding` above is the tenant's DEFAULT board under its old name, so every
                existing link and bookmark still lands. */}
            <Route path="boards/:workflowId" element={<WorkflowBoardRoute />} />
            {/* Static before the param route, for the same reason `onboarding/outstanding` is. */}
            <Route path="boards/:workflowId/import" element={<ImportPage />} />
            <Route path="boards/:workflowId/:jobId" element={<FileDetailPage />} />
            {/* ADR-122 — the firm's staff reword their clients' emails. The nav offers it only to
                someone the engine lists at least one email for; the routes render for anyone, and a
                person with nothing to change is told so in words (the engine refuses every write). */}
            <Route path="emails" element={<EmailsPage />} />
            <Route path="emails/:templateId" element={<EmailEditorPage />} />
            {/* Anything else — a stale bookmark, a link to a family this tenant no longer
                has — goes back to the landing route, which decides what they can see. */}
            <Route path="*" element={<BackToLanding />} />
          </Routes>
        </div>
      </main>
    </div>
  );
}

/**
 * THE ROUTE TESTIDS, AND WHY THEY LIVE ON THE PAGES.
 *
 * <p>Each onboarding route's page carries a `data-testid` on its ROOT — `onboarding-board`,
 * `onboarding-outstanding`, `onboarding-file-detail` — and that id is the route-order test's handle
 * on "which of these rendered" (`nav/PortalNav.test.tsx`). The ids name the ROUTE's job, not any
 * one page's content, so that the test which proves `/onboarding/outstanding` is not swallowed by
 * `/onboarding/:jobId` never has to be rewritten against page copy — a routing guarantee must not
 * start depending on a heading someone may reword. They were introduced on task 4.1's placeholders
 * with that instruction attached, and tasks 4.2 and 4.3 carried each one onto the real page, which
 * is why the routing test needed no change when the placeholders went.</p>
 *
 * <p>The root element also carries the id UNCONDITIONALLY — before the read resolves, while it is
 * loading, and in every failure state — so "did this route render" and "did its data arrive" stay
 * two different questions.</p>
 */

/**
 * The landing route: send a client straight to their first configured report.
 *
 * <p>A portal root that is its own page would be a page every client sees and nobody wants —
 * one more click between them and the numbers they came for. So `/` resolves to the first
 * entry in the nav, and only renders something of its own when there is nothing to send
 * them to.</p>
 */
function PortalLanding() {
  const { entries, isLoading, error } = usePortalNavEntries();
  // The query rides along so a `?tenant=` (walk 3) survives the redirect; for a client it is empty.
  const { search } = useLocation();

  if (isLoading) return <LoadingReports />;
  if (error) return <ReportsUnavailable error={error} />;

  const first = entries[0];
  if (first) return <Navigate to={{ pathname: first.route, search }} replace />;

  return <NoReportsYet />;
}

/** The catch-all: back to the landing route, keeping the query (and so any `?tenant=`). */
function BackToLanding() {
  const { search } = useLocation();
  return <Navigate to={{ pathname: "/", search }} replace />;
}

/** Quiet placeholder while `/me` and the families read resolve. */
function LoadingReports() {
  return (
    <div aria-busy="true" aria-live="polite">
      <div className="skeleton" style={{ width: "40%" }} />
      <div className="skeleton" style={{ width: "70%" }} />
      <div className="skeleton" />
    </div>
  );
}

/**
 * A correctly-provisioned client whose tenant has no report configured yet. Client
 * vocabulary throughout: this describes their situation, not the platform's mechanism.
 */
function NoReportsYet() {
  return (
    <div className="state" role="status">
      <h1 className="state__title">No reports configured yet</h1>
      <p className="state__body">
        Once your reports are set up they will appear here automatically, and you will get a
        link each time a new one is ready. Speak to your AR Dept contact if you were
        expecting to see something.
      </p>
    </div>
  );
}

/**
 * Something stopped us answering: a failed read, or a sign-in with no tenant behind it.
 *
 * <p>Both get client-appropriate copy — no status codes, no stack, no tenant identifiers.
 * The two are separated only because a client can act on one of them ("you are signed in as
 * X") and not the other; the diagnosis for the provisioning fault is already on the dev
 * console for whoever gets the call.</p>
 */
function ReportsUnavailable({ error }: { error: Error }) {
  const isProvisioning = error instanceof NoTenantAssignedError;
  return (
    <div className="state state--error" role="status">
      <h1 className="state__title">
        {isProvisioning ? "Your account is not set up yet" : "We could not load your reports"}
      </h1>
      <p className="state__body">
        {isProvisioning
          ? "Your sign-in worked, but no reports have been linked to your account. Please " +
            "contact your AR Dept contact so they can finish setting it up."
          : "Please try again in a moment. If it keeps happening, contact your AR Dept contact."}
      </p>
    </div>
  );
}

// ── The report routes, in the client's vocabulary (#1163 AC-4) ────────────────
// `/sales-activity` and `/vacancies` are the CLIENT's names for these two reports, and they
// are the route, the nav label (nav/familyRegistry.ts), the page heading and the browser tab
// (nav/usePageTitle.ts) alike — one label constant behind all four, so a rename cannot land
// in three places out of four.
//
// The platform's own names for the same two reports — the metric families these pages read,
// and the engine paths they read them from — stop at the wire. test/vocabulary-fence.test.ts
// fails the build if one reaches a string this bundle can render, with the protocol values
// exempted by exact string so a new client-visible use cannot hide behind one.
//
// Both pages are real: screens/SalesActivityPage.tsx reads the weekly grid + roster, and
// screens/VacanciesPage.tsx the role-level forecast payload. No placeholder is left here.
//
// ── The onboarding routes (#1939) ─────────────────────────────────────────────
// `/onboarding` is the same kind of decision one layer on: the firm's own word for the process,
// not the platform's (`job-status` is the wire family, `status_view` the declaration block, and
// neither reaches a screen). The label behind the route, the nav entry and the page heading is
// one constant — ONBOARDING_LABEL in nav/familyRegistry.ts.
//
// `/onboarding/:jobId` PUTS A JOB ID IN THE ADDRESS BAR, and that is deliberate and in scope:
// a URL segment is a routing key, not rendered copy (AC-5 says so explicitly). What the card
// forbids is a GUID in what a staffer READS, which is the pages' rule to keep (tasks 4.2/4.3
// sweep the rendered tree for one).
